// ============================
// File: src/pages/RequirementsPage.jsx
// ============================

import React, { useEffect, useState } from "react";
import { RequirementsBuilder } from "../features/requirements";
import DemandPageHeader from "../components/DemandPageHeader";
import DemandSelector from "../components/DemandSelector";
import AdvanceStageBar from "../components/AdvanceStageBar";
import { useDemandId } from "../context/DemandContext";
import { get } from "../api/http";

// Stage filter is "requirements": the backend writes current_stage = 'REQUIREMENTS'.
// This page previously matched on the exact string 'AI_REQUIREMENTS', which the
// backend never writes, so the list was always empty even when the fetch worked.
const STAGE = "requirements";

export default function RequirementsPage() {
  const { demandId: currentDemandId, setDemandId: handleDemandChange } = useDemandId();
  const [loading, setLoading] = useState(!currentDemandId);
  const [error, setError] = useState("");
  // Gates the advance control: moving to Security with no saved spec would hand the
  // pre-screen nothing to assess.
  const [savedVersion, setSavedVersion] = useState(0);

  useEffect(() => {
    if (currentDemandId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        // Was: fetch('http://localhost:8000/api/demands/list') — an absolute URL to
        // a port nothing listens on (the backend is 8007/8009 locally and behind an
        // ELB in production), so this request failed in every environment.
        const data = await get("/demands/list", { query: { limit: 200 } });
        if (cancelled) return;
        const inStage = (data?.demands || []).filter((d) =>
          d.current_stage?.toLowerCase().startsWith(STAGE)
        );
        if (inStage.length > 0) handleDemandChange(inStage[0].demand_id);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentDemandId, handleDemandChange]);

  if (error) {
    return (
      <div>
        <DemandPageHeader
          title="AI Requirements"
          description="AI-generated functional and non-functional requirements with acceptance criteria"
          breadcrumbPath="AI Requirements"
        >
          <DemandSelector currentDemandId={null} stage={STAGE} onDemandChange={handleDemandChange} />
        </DemandPageHeader>
        <div style={{ margin: 24, padding: 16, border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                      background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))', borderRadius: 8, color: 'var(--cares-orange)' }}>
          Could not load demands: {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '48px',
            height: '48px',
            border: '4px solid var(--cares-line-card)',
            borderTop: '4px solid var(--cares-purple)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '16px', fontSize: '16px', color: 'var(--cares-text-label)' }}>Loading AI Requirements demands...</p>
        </div>
      </div>
    );
  }

  if (!currentDemandId) {
    return (
      <div>
        <DemandPageHeader
          title="AI Requirements"
          description="AI-generated functional and non-functional requirements with acceptance criteria"
          breadcrumbPath="AI Requirements"
        >
          <DemandSelector
            currentDemandId={null}
            stage={STAGE}
            onDemandChange={handleDemandChange}
          />
        </DemandPageHeader>

        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            background: 'var(--cares-surface-card)',
            borderRadius: '16px',
            padding: '48px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            border: '1px solid var(--cares-line-card)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>📋</div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '12px' }}>
              No Demands in AI Requirements
            </h2>
            <p style={{ fontSize: '16px', color: 'var(--cares-text-label)', marginBottom: '32px' }}>
              There are currently no demands in the AI Requirements stage. Demands will appear here after completing the Discovery phase.
            </p>
            <button
              onClick={() => window.location.href = '/aieaai'}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--cares-purple)',
                color: 'var(--cares-text-max)',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DemandPageHeader
        title="AI Requirements"
        description="AI-generated functional and non-functional requirements with acceptance criteria"
        breadcrumbPath="AI Requirements"
      >
        <DemandSelector
          currentDemandId={currentDemandId}
          stage={STAGE}
          onDemandChange={handleDemandChange}
        />
      </DemandPageHeader>
      <RequirementsBuilder demandId={currentDemandId} onSaved={setSavedVersion} />
      {/* Requirements was the other dead end: saving a spec set current_stage to
          REQUIREMENTS but nothing moved it on to Security Pre-Screen. */}
      <div className="mx-auto max-w-7xl px-6 pb-6">
        <AdvanceStageBar
          demandId={currentDemandId}
          requirement={{
            met: savedVersion > 0,
            message: "Save a requirements spec before moving to Security Pre-Screen.",
          }}
        />
      </div>
    </div>
  );
}
